import { config } from "../app-env/common";
import { loadConnectionErrorPage } from "../error";
import { state } from "../state";

/**
 * Wraps fetch with an authorization header and generates a url based on the path and api url
 * @param {string} path
 * @param {RequestInit} req
 * @param {number} [timeout] milliseconds
 * @returns {Promise<Response>}
 */
async function apiFetch(path, req, timeout) {
	if (!timeout) return fetch(config.apiBaseUrl + path, req);

	// else with timeout
	const ctrl = new AbortController();
	req.signal = ctrl.signal;

	const abortJob = () => {
		ctrl.abort();
		loadConnectionErrorPage();
	};
	const timer = setTimeout(abortJob, timeout);

	return fetch(config.apiBaseUrl + path, req).finally(() => clearTimeout(timer));
}

/**
 * @typedef {Object} RequestOptions
 * @property {BodyInit} [body]
 * @property {number} [timeout] milliseconds
 */

/**
 * @param {string} method
 * @param {RequestOptions} [reqOpts]
 */
async function newRequest(method, reqOpts) {
	/** @type {RequestInit} */
	const req = {
		headers: {
			Authorization: `Bearer ${await state.getTokenString()}`,
		},
		method: method,
	};

	if (reqOpts) {
		if (reqOpts.body) req.body = reqOpts.body;
	}

	return req;
}

/**
 * Api DELETE request
 * @param {string} path
 * @param {RequestOptions} [reqOpts]
 * @returns {Promise<Response>}
 */
async function del(path, reqOpts) {
	const req = await newRequest("DELETE", reqOpts);
	if (reqOpts?.timeout) return apiFetch(path, req, reqOpts.timeout);
	else return apiFetch(path, req);
}

/**
 * Api GET request
 * @param {string} path
 * @param {RequestOptions} [reqOpts]
 * @returns {Promise<Response>}
 */
async function get(path, reqOpts) {
	const req = await newRequest("GET", reqOpts);
	if (reqOpts?.timeout) return apiFetch(path, req, reqOpts.timeout);
	else return apiFetch(path, req);
}

/**
 *  Api POST request
 * @param {string} path
 * @param {RequestOptions} [reqOpts]
 * @returns {Promise<Response>}
 */
async function post(path, reqOpts) {
	const req = await newRequest("POST", reqOpts);
	if (reqOpts?.timeout) return apiFetch(path, req, reqOpts.timeout);
	else return apiFetch(path, req);
}

/**
 * Api PUT request
 * @param {string} path
 * @param {RequestOptions} [reqOpts]
 * @returns {Promise<Response>}
 */
async function put(path, reqOpts) {
	const req = await newRequest("PUT", reqOpts);
	if (reqOpts?.timeout) return apiFetch(path, req, reqOpts.timeout);
	else return apiFetch(path, req);
}

export { del, get, post, put };
